import { LngLatBoundsLike } from 'maplibre-gl';
import { ViewState } from 'react-map-gl/maplibre';
import { DeckProps, PickingInfo, LayerProps, Layer } from '@deck.gl/core/typed';
import { ContextMenuItem } from 'components/maps/MapMarker';
import { PopoverPosition } from '@mui/material/Popover';
import { MapEntityClickEvent } from '../types';
import { Feature, Geometry, GeometryCollection, Point } from '@turf/helpers';
import { Override } from 'types/util';
import { EditableGeojsonLayerProps as NebulaEditableGeojsonLayerProps } from '@nebula.gl/layers/dist-types/layers/editable-geojson-layer';
import { KineticMarkerData, KineticGeoJsonData } from 'components/maps/MapMarkerContext';

export interface DeckGlMapContainerProps {
  bounds: LngLatBoundsLike;
  showMapMarkers?: boolean;
  cluster: boolean;
  height: number;
  width: number;
  viewState?: Partial<ViewState>;
  extraLayers?: Layer[];
  mapEntityClickEvents?: MapEntityClickEvent[];
  disableClusterSelection?: boolean;
  previewLayers?: Layer[];
  /**
   * Components to render under the map libre map
   */
  mapChildren?: React.ReactNode;
  contextChildren?: React.ReactNode;
  addToCache: (html: string) => void;
  deckProps?: Partial<DeckProps>;
  mapContextMenu?: ContextMenuItem[];

  // Passed down from BaseMap
  entityDrawer?: React.ReactNode;
  toolbar?: boolean;
  searchBox?: boolean;
  layerButtonPosition?: 'top' | 'bottom';
  layerDrawerPosition?: 'left' | 'right';
  searchBoxPosition?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
  zoomButtonsPosition?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
  disableExtraLayerClicks?: boolean;
}

export interface MapContextMenuProps {
  items: ContextMenuItem[];
  position: PopoverPosition | undefined;
  handleClose: () => void;
  coordinates?: number[];
}

export interface CursorState {
  /** Whether the cursor is over a pickable object */
  isHovering: boolean;
  /** Whether the cursor is down */
  isDragging: boolean;
}
export type PickedObject<TObject> = Override<PickingInfo, { picked?: boolean; object?: TObject }>;

// Types and predicates for different types of map event objects: For narrowing info.object?....
export type HasPoint = { geometry: Point };

export const isPointMapObject = (info: PickedObject<any>): info is PickedObject<HasPoint> => {
  return (
    'geometry' in (info.object ?? {}) && typeof info.object.geometry?.coordinates?.[0] === 'number'
  );
};

export interface EditableGeoJsonLayerProps extends NebulaEditableGeojsonLayerProps<any> {
  order: number;
}
export interface DeckGlLayerProps extends LayerProps {
  getLayerTooltip?: (info: PickingInfo) => string | null;
}

export type GeoJsonLayerType = Layer & { props: DeckGlLayerProps };

export type GeoJsonLayerPickingInfo = Override<PickingInfo, { layer: GeoJsonLayerType }>;

export type KineticFeature = Feature<
  Geometry | GeometryCollection,
  KineticMarkerData | KineticGeoJsonData
>;
